import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';

import clsx from 'clsx';

// MUI components
import {
  Box,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableCell,
  TableContainer,
  TableBody,
  TableHead,
  TableRow,
  Typography,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormHelperText,
  TableSortLabel,
} from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';

// MUI Icons
import RemoveRedEyeIcon from '@material-ui/icons/RemoveRedEye';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CancelIcon from '@material-ui/icons/Cancel';
import RestoreIcon from '@material-ui/icons//Restore';
import { parseCompleteDate, getDuration, shouldHighlightDuration } from '../../utils/date';

// components
import useStyles from './styles';
import { NOT_FOUND, UNAUTHORIZED } from '../../utils/const/http';

import Snackbar from '../../components/Snackbar';
import Pagination from '../../components/Pagination';

// API calls
import {
  getAllTaskResponsesPerPage,
  exportTaskResponses,
  exportTaskResponsesAuditReport,
  updateDeletedSurveyResponses,
} from '../../requests/api/task-response';

const getStoredResponsesFilters = (storageKey) => {
  if (!storageKey) {
    return {};
  }

  try {
    return JSON.parse(localStorage.getItem(storageKey)) ?? {};
  } catch {
    return {};
  }
};

// Sólo se envían los filtros con valor; los usan ambos exports de la página
const buildExportFilters = (filters) => ({
  ...(filters.id && { id: filters.id }),
  ...(filters.branch && { branch: filters.branch }),
  ...(filters.survey && { survey: filters.survey }),
  ...(filters.user && { user: filters.user }),
  ...(filters.dateStart && { dateStart: filters.dateStart }),
  ...(filters.dateEnd && { dateEnd: filters.dateEnd }),
});

// Descarga el Blob recibido mediante un enlace temporal
const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();

  window.URL.revokeObjectURL(url);
};

const Responses = () => {
  const styles = useStyles();
  const { t } = useTranslation();
  // The status column, the row-action icons and the filter buttons all set their
  // colours through inline `style` props, so they need the palette in JS.
  const theme = useTheme();
  const account = useSelector((state) => state.account);
  const userId = account?.user?.id;
  const responsesFiltersKey = userId ? `responsesFilters:${userId}` : null;

  const [page, setPage] = useState(0);
  const [rowsToShow, setRowsToShow] = useState(null);
  const rowsPerPage = 10;
  const [taskResponsesQuantity, setTaskResponsesQuantity] = useState(0);
  const [appliedFilters, setAppliedFilters] = useState(() => (
    getStoredResponsesFilters(responsesFiltersKey)
  ));
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportingAuditReport, setExportingAuditReport] = useState(false);
  const [err, setErr] = useState('');
  const [succ, setSucc] = useState('');
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  // Aplicar Orden
  const [sortOrder, setSortOrder] = useState('desc');
  const [sortBy, setSortBy] = useState('createdAt');

  const tableHeaders = [
    {
      id: 'surveyResponseId',
      label: <b>{t('qa.tableHeaders.surveyResponseId')}</b>,
    },
    {
      id: 'locationName',
      label: <b>{t('qa.tableHeaders.locationName')}</b>,
    },
    {
      id: 'taskName',
      label: <b>{t('qa.tableHeaders.taskName')}</b>,
    },
    {
      id: 'type',
      label: <b>{t('qa.tableHeaders.type')}</b>,
    },
    {
      id: 'category',
      label: <b>{t('qa.tableHeaders.category')}</b>,
    },
    {
      id: 'userName',
      label: <b>{t('qa.tableHeaders.userName')}</b>,
    },
    {
      id: 'userEmail',
      label: <b>{t('qa.tableHeaders.userEmail')}</b>,
    },
    {
      id: 'startedAt',
      label: <b>{t('qa.tableHeaders.startedAt')}</b>,
    },
    {
      id: 'finishedAt',
      label: <b>{t('qa.tableHeaders.finishedAt')}</b>,
    },
    {
      id: 'measurementTime',
      label: <b>{t('qa.tableHeaders.measurementTime')}</b>,
    },
    {
      id: 'createdAt',
      label: <b>{t('qa.tableHeaders.createdAt')} (UTC-0)</b>,
    },
    {
      id: 'startedAtReal',
      label: <b>{t('qa.tableHeaders.startedAtReal')}</b>,
    },
    {
      id: 'deleted',
      label: <b>{t('qa.tableHeaders.status')}</b>,
    },
    {
      id: 'deletedBy',
      label: <b>{t('qa.tableHeaders.deletedBy')}</b>,
    },
    {
      id: 'actions',
      label: <b>{t('qa.tableHeaders.actions')}</b>,
    },
    {
      id: 'edit',
      label: <b>{t('qa.tableHeaders.edit')}</b>,
    },
    {
      id: 'statusUpdate',
      label: <b>{t('qa.tableHeaders.statusUpdate')}</b>,
    },
  ];

  // Aplicar Filtros
  const [filters, setFilters] = useState(() => {
    const responsesFilters = getStoredResponsesFilters(responsesFiltersKey);
    return {
      id: responsesFilters.id ?? '',
      branch: responsesFilters.branch ?? '',
      survey: responsesFilters.survey ?? '',
      dateStart: responsesFilters.dateStart ?? '',
      dateEnd: responsesFilters.dateEnd ?? '',
      user: responsesFilters.user ?? '',
    };
  });

  const requestSearch = async (
    pageOfTable,
    limit,
    currentFilters,
    order = {},
  ) => {
    setLoading(true);

    // Construir filtros válidos
    const filters = {
      ...(currentFilters.id && { id: currentFilters.id }),
      ...(currentFilters.branch && { branch: currentFilters.branch }),
      ...(currentFilters.survey && { survey: currentFilters.survey }),
      ...(currentFilters.dateStart && { dateStart: currentFilters.dateStart }),
      ...(currentFilters.dateEnd && { dateEnd: currentFilters.dateEnd }),
      ...(currentFilters.user && { user: currentFilters.user }),
    };

    // Llamada a la API a través de la función ya existente
    const response = await getAllTaskResponsesPerPage(
      pageOfTable,
      limit,
      filters,
      order,
    );

    if (response.success) {
      setRowsToShow(response.data.data.taskResponses);
      setTaskResponsesQuantity(response.data.data.totalTaskResponses);
    }

    if (response.status === UNAUTHORIZED.status) {
      window.location.href = '/home';
    }

    setLoading(false);
  };

  const handleChangePage = (newPage) => {
    const numberNewPage = Number.parseInt(newPage, 10);
    setPage(numberNewPage);
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleOpenConfirmDialog = (row) => {
    setSelectedRow(row);
    setConfirmDialogOpen(true);
  };

  const handleConfirmToggleDeleted = async () => {
    if (!selectedRow) return;

    const response = await updateDeletedSurveyResponses(
      selectedRow.id,
      !selectedRow.deleted,
    );
    if (response.success) {
      setSucc(
        selectedRow.deleted ? 'Marcado como activo' : 'Marcado como eliminado',
      );
      requestSearch(page, rowsPerPage, appliedFilters);
    } else {
      setErr('No se pudo cambiar el estado');
    }

    setConfirmDialogOpen(false);
    setSelectedRow(null);
  };

  useEffect(() => {
    if (Object.keys(appliedFilters).length > 0) {
      requestSearch(page, rowsPerPage, appliedFilters, { sortBy, sortOrder });
    } else {
      requestSearch(page, rowsPerPage, {}, { sortBy, sortOrder });
    }
  }, [page, rowsPerPage, appliedFilters, sortBy, sortOrder]);

  // Exportar Información del proyecto
  const handleExport = async () => {
    setExporting(true);

    const result = await exportTaskResponses(buildExportFilters(filters));

    if (result.success) {
      downloadBlob(result.data, 'task-responses-export.xlsx');
      setSucc(t('qa.export.success'));
    } else {
      setErr(result.errorMessage || t('qa.export.error'));
    }

    setExporting(false);
  };

  // Reporte de auditoría: una fila por respuesta con su estado de revisión
  const handleAuditReport = async () => {
    setExportingAuditReport(true);

    const result = await exportTaskResponsesAuditReport(buildExportFilters(filters));

    if (result.success) {
      downloadBlob(result.data, 'reporte-auditoria.xlsx');
      setSucc('Reporte de auditoría generado exitosamente');
    } else if (result.status === NOT_FOUND.status) {
      setErr('No hay respuestas para los filtros seleccionados');
    } else if (result.errorMessage) {
      setErr(result.errorMessage);
    } else {
      setErr('Error al generar el reporte de auditoría');
    }

    setExportingAuditReport(false);
  };

  const handleSort = (columnId) => {
    if (sortBy === columnId) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(columnId);
      setSortOrder('asc');
    }
    setPage(0); // resetear página al ajustar orden
  };

  const renderStatus = (row) => {
    const statusStyle = {
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
    };

    if (row.deleted) {
      return (
        <span
          style={{
            ...statusStyle,
            color: theme.palette.custom.errorText,
          }}
        >
          <DeleteIcon fontSize="small" />
          {t('status.deleted')}
        </span>
      );
    }

    // Última revisión QA vigente; la API ya la adjunta por fila.
    if (row.SurveyResponseReview?.status === 'rejected') {
      return (
        <span
          style={{ ...statusStyle, color: theme.palette.custom.statusRejectedText }}
        >
          <CancelIcon fontSize="small" />
          {t('qa.status.rejected')}
        </span>
      );
    }

    return (
      <span style={{ ...statusStyle, color: theme.palette.custom.successIcon }}>
        <CheckCircleIcon fontSize="small" />
        {t('status.active')}
      </span>
    );
  };

  const content = () => {
    if (!taskResponsesQuantity) {
      return (
        <TableRow>
          <TableCell colSpan={9} className={styles.users}>
            <h4>{t('qa.noTaskResponses')}</h4>
          </TableCell>
        </TableRow>
      );
    }
    return (
      <>
        {rowsToShow.map((row) => {
          const label = row.TaskInstance?.Survey?.label;
          const highlight = shouldHighlightDuration(
            row.startedAt,
            row.finishedAt,
            label,
          );

          return (
            <TableRow hover key={row.id}>
              {row.TaskInstance.Survey && (
                <>
                  <TableCell align="left">{row.id}</TableCell>
                  <TableCell
                    component="th"
                    scope="row"
                    padding="none"
                    className={styles.locationName}
                    width="14%"
                  >
                    {row.TaskInstance.Location
                      ? row.TaskInstance.Location.name
                      : t('qa.noLocationName')}
                  </TableCell>
                  <TableCell
                    align="left"
                    scope="row"
                    padding="none"
                    className={styles.titleField}
                    width="14%"
                  >
                    <Link
                      to={`tasks/${row.TaskInstance.Survey.id}`}
                      style={{ textDecoration: 'none', color: 'inherit' }}
                    >
                      {row.TaskInstance.Survey.title
                      && row.TaskInstance.Survey.id
                        ? row.TaskInstance.Survey.title
                        : t('qa.noTaskName')}
                    </Link>
                  </TableCell>
                  <TableCell align="left">
                    {row.TaskInstance.Survey
                      ? row.TaskInstance.Survey.type
                      : t('qa.noTaskName')}
                  </TableCell>
                  <TableCell align="left">
                    {row.TaskInstance.Category
                      ? `${row.TaskInstance.Category.name} (${row.TaskInstance.Category.code})`
                      : 'General'}
                  </TableCell>
                  <TableCell align="left">
                    {row.TaskInstance.User
                      ? row.TaskInstance.User.fullName
                      : t('qa.noUserName')}
                  </TableCell>
                  <TableCell className={styles.userEmail} align="left">
                    {row.TaskInstance.User
                      ? row.TaskInstance.User.email
                      : t('qa.noUserEmail')}
                  </TableCell>
                  <TableCell align="center">
                    {parseCompleteDate(row.startedAt)}
                  </TableCell>
                  <TableCell align="center">
                    {parseCompleteDate(row.finishedAt)}
                  </TableCell>
                  <TableCell align="center">
                    <span
                      style={{
                        color: highlight
                          ? theme.palette.custom.errorText
                          : 'inherit',
                      }}
                    >
                      {getDuration(row.startedAt, row.finishedAt)}
                    </span>
                  </TableCell>
                  <TableCell align="center">
                    {parseCompleteDate(row.createdAt)}
                  </TableCell>
                  <TableCell align="center">
                    {parseCompleteDate(row.startedAtReal)}
                  </TableCell>
                  <TableCell align="center">
                    {renderStatus(row)}
                  </TableCell>
                  <TableCell align="center">
                    {row.deleted && row.DeletedByUser
                      ? row.DeletedByUser.fullName
                      : '-'}
                  </TableCell>
                  <TableCell align="center">
                    <IconButton component={Link} to={`responses/${row.id}`}>
                      <RemoveRedEyeIcon title={t('qa.review')} />
                    </IconButton>
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      component={Link}
                      to={`responses/${row.id}/edit`}
                    >
                      <EditIcon titleAccess="Editar" />
                    </IconButton>
                  </TableCell>
                  <TableCell align="center">
                    <IconButton onClick={() => handleOpenConfirmDialog(row)}>
                      {row.deleted ? (
                        <RestoreIcon
                          titleAccess="Reactivar"
                          style={{ color: theme.palette.custom.successIcon }}
                        />
                      ) : (
                        <DeleteIcon
                          titleAccess="Eliminar"
                          style={{ color: theme.palette.custom.errorText }}
                        />
                      )}
                    </IconButton>
                  </TableCell>
                </>
              )}
            </TableRow>
          );
        })}
      </>
    );
  };

  return (
    <div className={styles.content}>
      <div className={styles.header}>
        <Box sx={{ width: '100%' }}>
          <Typography variant="h1" className={styles.title} gutterBottom>
            {t('qa.title')}
          </Typography>
          <Typography
            variant="h2"
            className={styles.subtitle}
            gutterBottom
            display="inline"
          >
            {t('qa.subtitle')}
          </Typography>
        </Box>
      </div>
      <div className={styles.filtersContainer}>
        <Box sx={{ width: '100%', gap: '2' }} display="flex" flexWrap="wrap">
          <TextField
            className={styles.filtersId}
            label="ID"
            variant="outlined"
            size="small"
            value={filters.id}
            onChange={(e) => handleFilterChange('id', e.target.value)}
          />
          <TextField
            className={styles.filtersSucursal}
            label="Sucursal"
            variant="outlined"
            size="small"
            value={filters.branch}
            onChange={(e) => handleFilterChange('branch', e.target.value)}
          />
          <TextField
            className={styles.filtersEncuesta}
            label="Encuesta"
            variant="outlined"
            size="small"
            value={filters.survey}
            onChange={(e) => handleFilterChange('survey', e.target.value)}
          />
          <TextField
            className={styles.filtersEmail}
            label="Email"
            variant="outlined"
            size="small"
            value={filters.user}
            onChange={(e) => handleFilterChange('user', e.target.value)}
          />
          <TextField
            className={styles.filtersDateStart}
            label="Fecha de creación"
            type="date"
            InputLabelProps={{ shrink: true }}
            variant="outlined"
            size="small"
            value={filters.dateStart}
            onChange={(e) => handleFilterChange('dateStart', e.target.value)}
          />
          <TextField
            className={styles.filtersDateEnd}
            label="Fecha final (opcional)"
            type="date"
            InputLabelProps={{ shrink: true }}
            variant="outlined"
            size="small"
            value={filters.dateEnd}
            onChange={(e) => handleFilterChange('dateEnd', e.target.value)}
          />
          {/* '#888' -> `subheading` ('#828282'), a ~2% shift in light mode. */}
          <FormHelperText
            style={{
              color: theme.palette.custom.subheading,
              maxWidth: '26rem',
            }}
          >
            *Al exportar se aplicarán todos los filtros activos (ID, Sucursal,
            Encuesta, Email y Fechas).
          </FormHelperText>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            color="primary"
            size="medium"
            style={{ height: 40, marginBottom: 16 }}
            onClick={() => {
              if (responsesFiltersKey) {
                localStorage.setItem(responsesFiltersKey, JSON.stringify(filters));
              }
              setPage(0); // resetea página
              setAppliedFilters({ ...filters });
            }}
          >
            Aplicar filtros
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="medium"
            style={{
              height: 40,
              marginLeft: 8,
              marginBottom: 16,
              // '#d32f2f' is exactly MUI's `error.dark`.
              backgroundColor: theme.palette.error.dark,
              color: theme.palette.custom.solidButtonText,
            }}
            onClick={() => {
              if (responsesFiltersKey) {
                localStorage.removeItem(responsesFiltersKey);
              }
              setFilters({
                id: '',
                branch: '',
                survey: '',
                dateStart: '',
                dateEnd: '',
                user: '',
              });
              setAppliedFilters({});
              setPage(0);
            }}
          >
            Limpiar filtros
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="medium"
            style={{
              height: 40,
              marginBottom: 16,
              marginLeft: 8,
              backgroundColor: theme.palette.custom.exportButtonBackground,
              color: theme.palette.custom.solidButtonText,
            }}
            onClick={handleExport}
            disabled={exporting || exportingAuditReport}
            aria-busy={exporting}
            startIcon={
              exporting ? (
                <CircularProgress
                  size={20}
                  style={{ color: theme.palette.custom.solidButtonText }}
                />
              ) : null
            }
          >
            {exporting ? 'Exportando...' : 'Exportar Información'}
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="medium"
            style={{
              height: 40,
              marginBottom: 16,
              marginLeft: 8,
              backgroundColor: theme.palette.custom.exportButtonBackground,
              color: theme.palette.custom.solidButtonText,
            }}
            onClick={handleAuditReport}
            disabled={exporting || exportingAuditReport}
            aria-busy={exportingAuditReport}
            startIcon={
              exportingAuditReport ? (
                <CircularProgress
                  size={20}
                  style={{ color: theme.palette.custom.solidButtonText }}
                />
              ) : null
            }
          >
            {exportingAuditReport ? 'Generando...' : 'Reporte Auditoria'}
          </Button>
        </Box>
      </div>
      <Paper className={styles.taskResponseTable}>
        <>
          <TableContainer className={styles.tableContainer}>
            <Table
              aria-labelledby="tableTitle"
              size="medium"
              aria-label="enhanced table"
              className={styles.table}
            >
              <TableHead>
                <TableRow>
                  {tableHeaders.map((tableHeader) => (
                    <TableCell
                      key={tableHeader.id}
                      className={clsx({
                        [styles.tableHeader]: true,
                        [styles.taskNameHeader]: [
                          'surveyResponseId',
                          'locationName',
                          'taskName',
                          'userName',
                          'userEmail',
                          'category',
                        ].includes(tableHeader.id),
                      })}
                      padding="normal"
                      align="left"
                      style={{
                        cursor: ['actions', 'statusUpdate', 'edit', 'deletedBy'].includes(
                          tableHeader.id,
                        )
                          ? 'default'
                          : 'pointer',
                      }}
                    >
                      {['actions', 'statusUpdate', 'edit', 'deletedBy'].includes(
                        tableHeader.id,
                      ) ? (
                          tableHeader.label
                        ) : (
                        <TableSortLabel
                          active={sortBy === tableHeader.id}
                          direction={
                            sortBy === tableHeader.id ? sortOrder : 'asc'
                          }
                          onClick={() => handleSort(tableHeader.id)}
                        >
                          {tableHeader.label}
                        </TableSortLabel>
                        )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={9} className={styles.progress}>
                      <CircularProgress />
                      <h4>{t('qa.loading')}</h4>
                    </TableCell>
                  </TableRow>
                ) : (
                  content()
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Pagination
            setPage={setPage}
            handleChangePage={handleChangePage}
            page={page}
            rowsPerPage={rowsPerPage}
            quantity={taskResponsesQuantity}
          />
        </>
      </Paper>
      <div>
        <Snackbar
          open={err !== ''}
          message={err}
          severity="error"
          onClose={() => setErr('')}
        />
        <Snackbar
          open={succ !== ''}
          message={succ}
          severity="success"
          onClose={() => setSucc('')}
        />
      </div>
      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
      >
        <DialogTitle>
          {selectedRow?.deleted
            ? '¿Deseas reactivar esta respuesta?'
            : '¿Deseas eliminar esta respuesta?'}
        </DialogTitle>
        <DialogContent>
          Id de Respuesta: <strong>{selectedRow?.id}</strong>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmDialogOpen(false)}
            style={{ color: theme.palette.custom.errorText }}
          >
            Cancelar
          </Button>
          <Button onClick={handleConfirmToggleDeleted} color="primary">
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Responses;
